import { getSiteConfig } from '@shared/config';
import { z } from 'zod';
import type { LimitPeriod } from '../my-account/SaferGambling/types';

const DEPOSIT_LIMIT_AMOUNT_MIN = 1;
const DEPOSIT_LIMIT_SKIP_AMOUNT = -1;

const DEPOSIT_LIMIT_PERIOD_BY_VALUE: Record<number, LimitPeriod> = {
  0: 'MONTH',
  1: 'WEEK',
  2: 'DAY',
};

type DepositLimitTranslate = (key: string, values?: Record<string, string | number>) => string;

type DepositLimitSchemaOptions = {
  requiredMessage: string;
  rangeMessage: string;
  maxMessage?: string;
  unchangedMessage?: string;
  currentLimitInCents?: number;
};

function getDepositLimitPeriod(periodValue: number): LimitPeriod {
  return DEPOSIT_LIMIT_PERIOD_BY_VALUE[periodValue] ?? 'DAY';
}

function getDepositLimitAmountMax(period: LimitPeriod): number {
  return getSiteConfig().SAFER_GAMBLING.depositLimitAmountMax[period];
}

function createDepositLimitAmountRangeCheck(getMessage: (max: number) => string, { allowSkip = false } = {}) {
  return (data: { period: { value: number }; amount: number }, ctx: z.RefinementCtx) => {
    if (allowSkip && data.amount === DEPOSIT_LIMIT_SKIP_AMOUNT) {
      return;
    }
    const max = getDepositLimitAmountMax(getDepositLimitPeriod(data.period.value));
    if (data.amount >= DEPOSIT_LIMIT_AMOUNT_MIN && data.amount <= max) {
      return;
    }
    ctx.addIssue({ code: 'custom', message: getMessage(max), path: ['amount'], continue: false });
  };
}

function createSkippableDepositLimitSchema(t: DepositLimitTranslate) {
  return z
    .object({
      period: z.object({
        label: z.string(),
        value: z.number(),
      }),
      amount: z.preprocess(val => (val === '' ? NaN : Number(val)), z.number({ error: t('validation.depositLimit.required') })) as z.ZodType<number>,
    })
    .superRefine(createDepositLimitAmountRangeCheck(max => t('validation.depositLimit.range', { max }), { allowSkip: true }));
}

function createDepositLimitValueSchema(options: DepositLimitSchemaOptions) {
  const { requiredMessage, rangeMessage, maxMessage, unchangedMessage, currentLimitInCents } = options;

  const baseSchema = z
    .object({
      period: z.object({
        label: z.string(),
        value: z.number(),
      }),
      amount: z.preprocess(
        val => (val === '' ? NaN : Number(val)),
        z.number({ error: requiredMessage }).refine(val => val >= DEPOSIT_LIMIT_AMOUNT_MIN, {
          message: rangeMessage,
          abort: true,
        })
      ),
    })
    .superRefine(createDepositLimitAmountRangeCheck(() => maxMessage ?? rangeMessage));

  const hasUnchangedCheck = unchangedMessage !== undefined && currentLimitInCents !== undefined && currentLimitInCents > 0;
  if (hasUnchangedCheck) {
    return baseSchema.refine(
      data => {
        const newAmountInCents = Math.round(Number(data.amount) * 100);
        return newAmountInCents !== currentLimitInCents;
      },
      { message: unchangedMessage, path: ['amount'] }
    );
  }

  return baseSchema;
}

function getDepositLimitSchemaForDialog(
  t: DepositLimitTranslate,
  depositLimits: { limits?: Array<{ limitType: string; period: string; limitValue?: number }> } | null,
  periodValue: number
) {
  const periodKey = getDepositLimitPeriod(periodValue);
  const currentLimit = depositLimits?.limits?.find(limit => limit.limitType === 'DEPOSIT' && limit.period === periodKey)?.limitValue;

  return createDepositLimitValueSchema({
    requiredMessage: t('validation.depositLimit.required'),
    rangeMessage: t('validation.depositLimit.range', { max: getDepositLimitAmountMax(periodKey) }),
    unchangedMessage: t('validation.depositLimit.unchanged'),
    currentLimitInCents: currentLimit,
  });
}

function getDepositLimitSchemaForForm(
  t: (key: string) => string,
  options: {
    requiredMessage: string;
    rangeMessage: string;
    maxMessage?: string;
    unchangedMessage?: string;
    originalLimitInCents?: number;
  }
) {
  return z.object({
    depositLimits: createDepositLimitValueSchema({
      requiredMessage: options.requiredMessage,
      rangeMessage: options.rangeMessage,
      maxMessage: options.maxMessage,
      unchangedMessage: options.unchangedMessage,
      currentLimitInCents: options.originalLimitInCents,
    }),
  });
}

export {
  createDepositLimitValueSchema,
  createSkippableDepositLimitSchema,
  getDepositLimitAmountMax,
  getDepositLimitPeriod,
  getDepositLimitSchemaForDialog,
  getDepositLimitSchemaForForm,
};
export type { DepositLimitTranslate };
